import React, {
	memo,
	Suspense,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { createPortal } from 'react-dom';

import RichTextErrorBoundary from '../ErrorBoundary/RichTextErrorBoundary';
import './RTEditor.css';

import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';

import {
	$getRoot,
	$getSelection,
	$isRangeSelection,
	$createParagraphNode,
	$createTextNode,
	$isElementNode,
	$isParagraphNode,
	createCommand,
	FORMAT_TEXT_COMMAND,
	SELECTION_CHANGE_COMMAND,
	COMMAND_PRIORITY_HIGH,
	COMMAND_PRIORITY_LOW,
} from 'lexical';

import {
	HeadingNode,
	QuoteNode,
	$isHeadingNode,
	$createHeadingNode,
} from '@lexical/rich-text';

import {
	ListItemNode,
	ListNode,
	INSERT_UNORDERED_LIST_COMMAND,
	INSERT_ORDERED_LIST_COMMAND,
	REMOVE_LIST_COMMAND,
} from '@lexical/list';

import { LinkNode, TOGGLE_LINK_COMMAND, $isLinkNode } from '@lexical/link';

import { $getNearestNodeOfType } from '@lexical/utils';

import {
	Heading1,
	Heading2,
	Heading3,
	Bold,
	Italic,
	Underline,
	Code2,
	List as ListIcon,
	ListOrdered,
	Link2,
	Check,
	Unlink,
} from 'lucide-react';
import { TextField, IconButton, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';

// ==================== LAZY IMPORTS ====================

const ListPluginLazy = React.lazy(() =>
	import('@lexical/react/LexicalListPlugin').then((m) => ({
		default: m.ListPlugin,
	}))
);


// ==================== THEME CONFIGURATION ====================

/** Lexical theme mapping to CSS classes */
const LEXICAL_THEME = {
	text: {
		bold: 'rte-text-bold',
		italic: 'rte-text-italic',
		underline: 'rte-text-underline',
		code: 'rte-text-code',
	},
	heading: {
		h1: 'rte-heading-h1',
		h2: 'rte-heading-h2',
		h3: 'rte-heading-h3',
	},
	list: {
		ul: 'rte-list-ul',
		ol: 'rte-list-ol',
		listitem: 'rte-list-item',
		nested: { listitem: 'rte-list-nested' },
	},
	link: 'rte-link',
};

// ==================== CONSTANTS ====================

/** Default features configuration */
const DEFAULT_FEATURES = {
	bold: true,
	italic: true,
	underline: true,
	code: true,
	list: true,
	link: true,
	headings: ['h1', 'h2', 'h3'],
};

/** Approximate line height in pixels for height calculations */
const ROW_HEIGHT_PX = 28;

/** Vertical padding for height calculations */
const EDITOR_PADDING_Y = 24;

/** Custom command to force open the link editor */
const OPEN_LINK_EDITOR_COMMAND = createCommand();

// ==================== UTILITY FUNCTIONS ====================


/**
 * Normalize a URL string to include https protocol if missing
 * @param input - Raw URL input
 * @returns Normalized URL with https protocol
 */
const normalizeHttpsUrl = (input) => {
	const trimmed = input.trim();
	if (!trimmed) return '';
	return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

/**
 * Validate if a URL string represents a valid public HTTPS URL
 * @param input - URL string to validate
 * @returns True if valid public HTTPS URL
 */
const isValidPublicUrl = (input) => {
	let url;
	try {
		url = new URL(normalizeHttpsUrl(input));
	} catch {
		return false;
	}

	if (url.protocol !== 'https:') return false;

	const hostname = url.hostname;
	if (!hostname.includes('.')) return false; // Reject single-label hosts

	// Basic TLD validation
	const tld = hostname.split('.').pop() || '';
	if (!/^[a-z]{2,63}$/i.test(tld)) return false;

	return true;
};

/**
 * Calculate editor height constraints based on row configuration
 * @param minRows - Minimum visible rows
 * @param maxRows - Maximum visible rows
 * @returns Object with minHeight and maxHeight in pixels
 */
const calculateEditorHeight = (minRows, maxRows) => {
	const minHeight = Math.max(48, minRows * ROW_HEIGHT_PX + EDITOR_PADDING_Y);
	const maxHeight = Math.max(
		minHeight,
		maxRows * ROW_HEIGHT_PX + EDITOR_PADDING_Y
	);

	return { minHeight, maxHeight };
};

// ==================== SUB-COMPONENTS ====================

/**
 * Reusable toolbar button component with consistent styling and accessibility
 */
const ToolbarButton = memo(function ToolbarButton({
	active = false,
	className = '',
	children,
	tooltip,
	disabled = false,
	...buttonProps
}) {
	return (
		<button
			type='button'
			className={`rte-toolbar-button ${active ? 'active' : ''} ${className}`}
			aria-pressed={active}
			title={tooltip}
			disabled={disabled}
			{...buttonProps}
		>
			{children}
		</button>
	);
});

/**
 * Feature gate plugin that prevents disabled commands from executing
 */
const FeatureGatePlugin = memo(function FeatureGatePlugin({
	features,
}) {
	const [editor] = useLexicalComposerContext();

	useEffect(() => {
		const unsubscribers = [];

		// Block text formatting commands for disabled features
		unsubscribers.push(
			editor.registerCommand(
				FORMAT_TEXT_COMMAND,
				(format) => {
					const featureMap = {
						bold: features.bold,
						italic: features.italic,
						underline: features.underline,
						code: features.code,
					};

					// Return true to swallow the command if feature is disabled
					return !featureMap[format];
				},
				COMMAND_PRIORITY_HIGH
			)
		);

		// Block link commands if link feature is disabled
		if (!features.link) {
			unsubscribers.push(
				editor.registerCommand(
					TOGGLE_LINK_COMMAND,
					() => true, // Swallow command
					COMMAND_PRIORITY_HIGH
				)
			);
		}

		// Block list commands if list feature is disabled
		if (!features.list) {
			unsubscribers.push(
				editor.registerCommand(
					INSERT_UNORDERED_LIST_COMMAND,
					() => true,
					COMMAND_PRIORITY_HIGH
				),
				editor.registerCommand(
					INSERT_ORDERED_LIST_COMMAND,
					() => true,
					COMMAND_PRIORITY_HIGH
				),
				editor.registerCommand(
					REMOVE_LIST_COMMAND,
					() => true,
					COMMAND_PRIORITY_HIGH
				)
			);
		}

		return () => {
			unsubscribers.forEach((unsub) => unsub());
		};
	}, [editor, features]);

	return null;
});

/**
 * Plugin to normalize nested paragraph structures that can occur in rich text editing
 */
const NormalizeParagraphsPlugin = memo(function NormalizeParagraphsPlugin() {
	const [editor] = useLexicalComposerContext();

	useEffect(() => {
		return editor.registerUpdateListener(() => {
			editor.update(() => {
				const root = $getRoot();
				const nodesToProcess = [...root.getChildren()];

				for (const node of nodesToProcess) {
					// Traverse the tree depth-first
					if ('getChildren' in node && typeof node.getChildren === 'function') {
						nodesToProcess.push(...(node).getChildren());
					}

					// Fix nested paragraphs by moving them to be siblings
					if ($isParagraphNode(node)) {
						for (const child of node.getChildren()) {
							if ($isParagraphNode(child)) {
								child.remove();
								node.insertAfter(child);
							}
						}
					}
				}
			});
		});
	}, [editor]);

	return null;
});

/**
 * Main toolbar component with all formatting controls
 */
const EditorToolbar = memo(function EditorToolbar({ features }) {
	const [editor] = useLexicalComposerContext();

	// Selection state tracking
	const [activeHeading, setActiveHeading] = useState(null);
	const [formatStates, setFormatStates] = useState({
		bold: false,
		italic: false,
		underline: false,
		code: false,
	});
	const [listStates, setListStates] = useState({
		bullet: false,
		ordered: false,
	});

	// Track selection changes to update toolbar button states
	useEffect(() => {
		return editor.registerCommand(
			SELECTION_CHANGE_COMMAND,
			() => {
				const selection = $getSelection();
				if (!$isRangeSelection(selection)) return false;

				// Update text format states
				setFormatStates({
					bold: selection.hasFormat('bold'),
					italic: selection.hasFormat('italic'),
					underline: selection.hasFormat('underline'),
					code: selection.hasFormat('code'),
				});

				// Find current block element for heading state
				let element = selection.anchor.getNode();
				if (element.getKey() === 'root') {
					// Handle root case
				} else {
					element = element.getTopLevelElementOrThrow();
				}

				// Handle text nodes by getting their parent
				if (element.getType() === 'text') {
					element = element.getParent();
				}

				// Update heading state
				setActiveHeading(
					$isHeadingNode(element) ? (element.getTag()) : null
				);

				// Update list states
				const listNode = $getNearestNodeOfType(
					selection.anchor.getNode(),
					ListNode
				);
				if (listNode) {
					const listType = (listNode).getListType?.();
					setListStates({
						bullet: listType === 'bullet',
						ordered: listType === 'number',
					});
				} else {
					setListStates({ bullet: false, ordered: false });
				}

				return false;
			},
			COMMAND_PRIORITY_LOW
		);
	}, [editor]);

	// Helper to find nearest block element (paragraph or heading)
	const findNearestBlock = useCallback(
		(node) => {
			let current = $isElementNode(node)
				? node
				: node.getParent();

			while (current) {
				if ($isParagraphNode(current) || $isHeadingNode(current)) {
					return current;
				}
				current = current.getParent();
			}

			return null;
		},
		[]
	);

	// Text formatting handlers
	const toggleTextFormat = useCallback(
		(format) => {
			editor.dispatchCommand(FORMAT_TEXT_COMMAND, format);
		},
		[editor]
	);

	// Heading toggle handler
	const toggleHeading = useCallback(
		(level) => {
			editor.update(() => {
				const selection = $getSelection();
				if (!$isRangeSelection(selection)) return;

				const block = findNearestBlock(selection.anchor.getNode());
				if (!block) return;

				if ($isHeadingNode(block) && block.getTag() === level) {
					// Convert heading to paragraph
					const paragraph = $createParagraphNode();
					block.getChildren().forEach((child) => paragraph.append(child));
					block.replace(paragraph);
					paragraph.selectEnd();
				} else {
					// Convert to heading
					const heading = $createHeadingNode(level);
					block.getChildren().forEach((child) => heading.append(child));
					block.replace(heading);
					heading.selectEnd();
				}
			});
		},
		[editor, findNearestBlock]
	);

	// List handlers
	const toggleBulletList = useCallback(() => {
		editor.update(() => {
			const selection = $getSelection();
			if (!$isRangeSelection(selection)) return;

			const listNode = $getNearestNodeOfType(
				selection.anchor.getNode(),
				ListNode
			);
			if (listNode && listNode.getListType?.() === 'bullet') {
				editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
			} else {
				editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
			}
		});
	}, [editor]);

	const toggleOrderedList = useCallback(() => {
		editor.update(() => {
			const selection = $getSelection();
			if (!$isRangeSelection(selection)) return;

			const listNode = $getNearestNodeOfType(
				selection.anchor.getNode(),
				ListNode
			);
			if (listNode && listNode.getListType?.() === 'number') {
				editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
			} else {
				editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
			}
		});
	}, [editor]);

	// Link handler
	const handleLinkClick = useCallback(() => {
		editor.update(() => {
			const selection = $getSelection();
			if (!$isRangeSelection(selection)) return;

			// Check if we're inside a link node
			let node = selection.anchor.getNode();
			while (node) {
				if ($isLinkNode(node)) {
					// Already in a link, open editor
					queueMicrotask(() => {
						editor.dispatchCommand(OPEN_LINK_EDITOR_COMMAND, undefined);
					});
					return;
				}
				node = node.getParent();
			}

			// Not in a link - require text selection to create one
			if (selection.isCollapsed()) return;

			// Create link and open editor
			editor.dispatchCommand(TOGGLE_LINK_COMMAND, 'https://');
			queueMicrotask(() => {
				editor.dispatchCommand(OPEN_LINK_EDITOR_COMMAND, undefined);
			});
		});
	}, [editor]);

	// Render heading buttons
	const renderHeadingButtons = () => {
		const iconMap = {
			h1: <Heading1 size={16} />,
			h2: <Heading2 size={16} />,
			h3: <Heading3 size={16} />,
		};

		return features.headings.map((level) => (
			<ToolbarButton
				key={level}
				active={activeHeading === level}
				onClick={() => toggleHeading(level)}
				tooltip={level.toUpperCase()}
			>
				{iconMap[level]}
			</ToolbarButton>
		));
	};

	return (
		<div
			className='rte-toolbar'
			role='toolbar'
			aria-label='Text formatting toolbar'
		>
			{/* Heading controls */}
			{renderHeadingButtons()}

			{/* Text formatting controls */}
			{features.bold && (
				<ToolbarButton
					active={formatStates.bold}
					onClick={() => toggleTextFormat('bold')}
					tooltip='Bold (Ctrl+B)'
				>
					<Bold size={16} />
				</ToolbarButton>
			)}

			{features.italic && (
				<ToolbarButton
					active={formatStates.italic}
					onClick={() => toggleTextFormat('italic')}
					tooltip='Italic (Ctrl+I)'
				>
					<Italic size={16} />
				</ToolbarButton>
			)}

			{features.underline && (
				<ToolbarButton
					active={formatStates.underline}
					onClick={() => toggleTextFormat('underline')}
					tooltip='Underline (Ctrl+U)'
				>
					<Underline size={16} />
				</ToolbarButton>
			)}

			{features.code && (
				<ToolbarButton
					active={formatStates.code}
					onClick={() => toggleTextFormat('code')}
					tooltip='Inline Code'
				>
					<Code2 size={16} />
				</ToolbarButton>
			)}

			{/* List controls */}
			{features.list && (
				<>
					<ToolbarButton
						active={listStates.bullet}
						onClick={toggleBulletList}
						tooltip='Bullet List'
					>
						<ListIcon size={16} />
					</ToolbarButton>

					<ToolbarButton
						active={listStates.ordered}
						onClick={toggleOrderedList}
						tooltip='Numbered List'
					>
						<ListOrdered size={16} />
					</ToolbarButton>
				</>
			)}

			{/* Link control */}
			{features.link && (
				<ToolbarButton
					onMouseDown={(e) => e.preventDefault()} // Preserve selection
					onClick={handleLinkClick}
					tooltip='Insert/Edit Link'
				>
					<Link2 size={16} />
				</ToolbarButton>
			)}
		</div>
	);
});

// Styled components for MUI integration - True floating editor
const StyledFloatingEditor = styled(Paper)(({ theme }) => ({
    position: 'fixed',
	zIndex: 10000,
	padding: theme.spacing(1),
	display: 'flex',
	alignItems: 'center',
	gap: theme.spacing(1),
	width: 400, // Fixed width so action icons can sit flush-right
	borderRadius: 8,
	boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)',
	background: theme.palette.background.paper,
	pointerEvents: 'auto',
	border: `1px solid ${theme.palette.divider}`,
    // Add a subtle animation
    transform: 'translateY(-2px)',
    transition: 'all 0.2s ease-out',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
	'& .MuiOutlinedInput-root': {
		height: 36,
		fontSize: '14px',
	},
}));

/**
 * Floating link editor for creating and modifying links
 */
const FloatingLinkEditor = memo(
	function FloatingLinkEditor() {
		const [editor] = useLexicalComposerContext();

		const [isOpen, setIsOpen] = useState(false);
		const [url, setUrl] = useState('');
		const [position, setPosition] = useState({ top: 0, left: 0 });
		const inputRef = useRef(null);
		const suppressNextOpenRef = useRef(false);
		const [linkNode, setLinkNode] = useState(null);
		const floatingRef = useRef(null);


		// Get selection position for floating editor
    const getSelectionPosition = useCallback(() => {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return null;

        // Prefer the DOM element for the active link when available
        let linkRect = null;
        try {
            if (linkNode) {
                const domElem = editor.getElementByKey?.(linkNode.getKey?.());
                if (domElem) {
                    linkRect = domElem.getBoundingClientRect();
                }
            }
        } catch {
            // ignore and fall back to selection range
        }

        if (linkRect) {
            return {
                top: linkRect.bottom + 8,
                left: linkRect.left, // align to start of link text
            };
        }

        const range = selection.getRangeAt(0);
        const rect = range.getBoundingClientRect();

        if (rect.width === 0 && rect.height === 0) {
            // Try getting from selected text node
            const node = selection.anchorNode;
            if (node && node.parentElement) {
                const parentRect = node.parentElement.getBoundingClientRect();
                return {
                    top: parentRect.bottom + 8,
                    left: parentRect.left,
                };
            }
        }

        return {
            top: rect.bottom + 8,
            left: rect.left, // align to selection start
        };
    }, [editor, linkNode]);

		// Auto-show when selection is inside a link
		useEffect(() => {
			return editor.registerUpdateListener(({ editorState }) => {
				editorState.read(() => {
					const selection = $getSelection();
					if (!$isRangeSelection(selection)) return;

					let node = selection.anchor.getNode();
					let foundLinkNode = null;

					// Find link node in ancestry
					while (node) {
						if ($isLinkNode(node)) {
							foundLinkNode = node;
							break;
						}
						node = node.getParent();
					}

					if (foundLinkNode) {
						if (suppressNextOpenRef.current) {
							suppressNextOpenRef.current = false;
							setIsOpen(false);
							return;
						}

						setUrl(foundLinkNode.getURL?.() || '');
						setLinkNode(foundLinkNode);
						
                    // Calculate position based on actual link element/selection
                    const pos = getSelectionPosition();
                    if (pos) {
                        setPosition({
                            top: Math.max(10, pos.top),
                            left: Math.max(20, Math.min(pos.left, window.innerWidth - 420)),
                        });
                        setIsOpen(true);
                    }
					} else {
						setIsOpen(false);
						setLinkNode(null);
					}
				});
			});
		}, [editor, getSelectionPosition]);

		// Handle force-open command from toolbar
		useEffect(() => {
			return editor.registerCommand(
				OPEN_LINK_EDITOR_COMMAND,
                () => {
                    suppressNextOpenRef.current = false;

					let currentUrl = '';
					let hasLink = false;
					let foundLinkNode = null;

					editor.getEditorState().read(() => {
						const selection = $getSelection();
						if (!$isRangeSelection(selection)) return;

						let node = selection.anchor.getNode();
						while (node) {
							if ($isLinkNode(node)) {
								hasLink = true;
								foundLinkNode = node;
								currentUrl = node.getURL?.() || '';
								break;
							}
							node = node.getParent();
						}
					});

					if (!hasLink) return false;

                    setUrl(currentUrl);
                    setLinkNode(foundLinkNode);

                    // Position next to the link
                    const pos = getSelectionPosition();
                    if (pos) {
                        setPosition({
                            top: Math.max(10, pos.top),
                            left: Math.max(20, Math.min(pos.left, window.innerWidth - 420)),
                        });
                    }

                    setIsOpen(true);

					return true;
				},
				COMMAND_PRIORITY_LOW
			);
		}, [editor, getSelectionPosition]);

    // Update position on scroll/resize
    useEffect(() => {
        if (!isOpen) return;

        const updatePosition = () => {
            const pos = getSelectionPosition();
            if (pos) {
                setPosition({
                    top: Math.max(10, pos.top),
                    left: Math.max(20, Math.min(pos.left, window.innerWidth - 420)),
                });
            }
        };

        // Initial sync
        updatePosition();

        window.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition);

        return () => {
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [isOpen, getSelectionPosition]);

		// Focus input when opened
		useEffect(() => {
			if (isOpen) {
				setTimeout(() => inputRef.current?.focus(), 0);
			}
		}, [isOpen]);

		// Apply URL changes
		const applyLink = useCallback(() => {
			const normalizedUrl = normalizeHttpsUrl(url);
			suppressNextOpenRef.current = true;

			if (linkNode) {
				editor.update(() => {
					linkNode.setURL?.(normalizedUrl);
				});
			}

			setIsOpen(false);
				inputRef.current?.blur();
		}, [editor, url, linkNode]);

		// Remove link
		const removeLink = useCallback(() => {
			suppressNextOpenRef.current = true;
			editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
			setIsOpen(false);
				inputRef.current?.blur();
		}, [editor]);

		// Handle popover close
		const handleClose = useCallback(() => {
			suppressNextOpenRef.current = true;
			setIsOpen(false);
			}, []);

		// Handle keyboard shortcuts
		const handleKeyDown = useCallback(
			(event) => {
				if (event.key === 'Enter') {
					event.preventDefault();
					if (isValidPublicUrl(url)) {
						applyLink();
					}
				} else if (event.key === 'Escape') {
					handleClose();
				}
			},
			[url, applyLink, handleClose]
		);

		// Handle click outside to close
		useEffect(() => {
			if (!isOpen) return;

			const handleClickOutside = (event) => {
				if (floatingRef.current && !floatingRef.current.contains(event.target)) {
					handleClose();
				}
			};

			document.addEventListener('mousedown', handleClickOutside);
			return () => document.removeEventListener('mousedown', handleClickOutside);
		}, [isOpen, handleClose]);

		if (!isOpen) return null;

		// Render floating editor using portal
		return createPortal(
			<StyledFloatingEditor
				ref={floatingRef}
				role='dialog'
				aria-label='Edit link URL'
				style={{
					top: position.top,
					left: position.left,
				}}
				onMouseDown={(e) => e.preventDefault()} // Prevent losing focus
			>
				<StyledTextField
					inputRef={inputRef}
					type='url'
					placeholder='https://example.com'
					value={url}
					onChange={(e) => setUrl(e.target.value)}
					onKeyDown={handleKeyDown}
					variant='outlined'
					size='small'
					InputProps={{
						'aria-label': 'Link URL',
					}}
					sx={{ flex: 1, minWidth: 220 }} // Grow to take available space, leaving actions flush-right
					autoFocus
				/>

				{/* Actions pinned to the extreme right */}
				<div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>
					<IconButton
						onClick={applyLink}
						disabled={!isValidPublicUrl(url)}
						title='Apply link'
						size='small'
						color='success'
						onMouseDown={(e) => e.preventDefault()}
						sx={{
							width: 28,
							height: 28,
							'& svg': { strokeWidth: 2 },
						}}
					>
						<Check size={16} />
					</IconButton>

					<IconButton
						onClick={removeLink}
						title='Remove link'
						size='small'
						color='error'
						onMouseDown={(e) => e.preventDefault()}
						sx={{
							width: 28,
							height: 28,
							'& svg': { strokeWidth: 2 },
						}}
					>
						<Unlink size={16} />
					</IconButton>
				</div>
			</StyledFloatingEditor>,
			document.body
		);
	}
);

/**
 * Plugin to handle initial content loading from JSONB database field or form JSON string
 */
const InitialValuePlugin = memo(function InitialValuePlugin({
	value,
	onInitialized,
}) {
	const [editor] = useLexicalComposerContext();
	const hasInitializedRef = useRef(false);

	useEffect(() => {
		if (hasInitializedRef.current) return;

		editor.update(() => {
			const root = $getRoot();
			root.clear();

			if (!value || value === '') {
				hasInitializedRef.current = true;
				onInitialized?.();
				return;
			}

			try {
				const parsed = typeof value === 'string' ? JSON.parse(value) : value;
				if (parsed?.root?.children) {
					const editorState = editor.parseEditorState(parsed);
					editor.setEditorState(editorState);
					hasInitializedRef.current = true;
					onInitialized?.();
					return;
				}
			} catch {
				// Fall through to plain text handling
			}

			const text = typeof value === 'string' ? value : '';
			if (text.trim()) {
				const paragraph = $createParagraphNode();
				paragraph.append($createTextNode(text));
				root.append(paragraph);
			}

			hasInitializedRef.current = true;
			onInitialized?.();
		});
	}, [editor, value, onInitialized]);

	return null;
});

// ==================== MAIN COMPONENT ====================

/**
 * RTEditor - A comprehensive rich text editor built on Lexical
 *
 * Features:
 * - Text formatting (bold, italic, underline, inline code)
 * - Headings (H1, H2, H3)
 * - Lists (bullet and numbered)
 * - Links (manual creation with floating editor)
 * - Configurable feature set
 * - TypeScript support
 * - Accessibility compliant
 * - TailwindCSS styling
 *
 * @example
 * ```tsx
 * const [content, setContent] = useState('');
 *
 * return (
 *   <RTEditor
 *     value={content}
 *     onChange={setContent}
 *     placeholder="Start writing..."
 *     features={{
 *       bold: true,
 *       italic: true,
 *       headings: ['h1', 'h2'],
 *       link: true,
 *     }}
 *   />
 * );
 * ```
 */
export default function RTEditor({
	value,
	onChange,
	placeholder = 'Start typing…',
	disabled = false,
	minRows = 4,
	maxRows = 12,
	heightMode = 'clamp',
	features: userFeatures,
	className = '',
	name,
	variant = 'default',
	error = false,
	onFocus,
	onBlur,
}) {
	// Merge user features with defaults
	const features = useMemo(
		() => ({
			...DEFAULT_FEATURES,
			...userFeatures,
			headings: userFeatures?.headings ?? DEFAULT_FEATURES.headings,
		}),
		[userFeatures]
	);

	// Calculate height constraints
	const { minHeight, maxHeight } = useMemo(
		() => calculateEditorHeight(minRows, maxRows),
		[minRows, maxRows]
	);

	// Helpers to detect whether serialized content includes any visible text
	const hasTextInNodes = useCallback((nodes) => {
		if (!Array.isArray(nodes) || nodes.length === 0) return false;

		for (const node of nodes) {
			if (node == null || typeof node !== 'object') continue;

			if (node.type === 'text' && typeof node.text === 'string' && node.text.trim().length > 0) {
				return true;
			}

			if (Array.isArray(node.children) && hasTextInNodes(node.children)) {
				return true;
			}
		}

		return false;
	}, []);

	// Editor configuration
	const initialConfig = useMemo(
		() => ({
			namespace: 'RTEditor',
			editable: !disabled,
			onError: (error) => {
				console.error('RTEditor error:', error);
			},
			nodes: [HeadingNode, QuoteNode, ListNode, ListItemNode, LinkNode],
			theme: LEXICAL_THEME,
		}),
		[disabled]
	);

	// Track previous serialized value and whether initial content is ready
	const previousOutputRef = useRef(typeof value === 'string' ? value : '');
	const [initialValueApplied, setInitialValueApplied] = useState(false);

	useEffect(() => {
		const normalized = typeof value === 'string' ? value : '';
		previousOutputRef.current = normalized;
	}, [value]);

	// Handle content changes
	const handleChange = useCallback(
		(editorState) => {
			const json = editorState.toJSON();
			const rootChildren = json?.root?.children;
			const hasAnyText = hasTextInNodes(rootChildren);

			if (!initialValueApplied) {
				if (!hasAnyText) {
					return;
				}
				setInitialValueApplied(true);
			}

			const serialized = hasAnyText ? JSON.stringify(json) : '';

			if (!hasAnyText && previousOutputRef.current === '') {
				return; // Remain empty without triggering onChange
			}

			if (serialized !== previousOutputRef.current) {
				previousOutputRef.current = serialized;
				onChange(serialized);
			}
		},
		[hasTextInNodes, initialValueApplied, onChange, setInitialValueApplied]
	);

	// Container styling based on variant and state
	const containerClasses = useMemo(() => {
		const classes = ['rte-container-default'];
		
		if (variant === 'plain') {
			classes.push('rte-container-plain');
		}
		
		if (error) {
			classes.push('rte-container-error');
		}

		return classes.join(' ');
	}, [variant, error]);

	// Content area styling - account for sticky toolbar
	const contentAreaStyles = useMemo(() => {
		if (heightMode === 'clamp') {
			// Subtract toolbar height (approximately 52px) from max height for scrollable area
			return {
				maxHeight: maxHeight - 52,
				overflowY: 'auto',
			};
		}
		return {};
	}, [heightMode, maxHeight]);

	// Content editable styling
	const editableClasses = useMemo(
		() =>
			[
				'rte-editable',
				disabled ? 'disabled' : '',
			]
				.filter(Boolean)
				.join(' '),
		[disabled]
	);


	return (
		<RichTextErrorBoundary>
			<div className={className}>
				<div className={containerClasses} style={{ minHeight }}>
					<LexicalComposer initialConfig={initialConfig}>
					{/* Toolbar */}
					<EditorToolbar features={features} />

					{/* Editor Surface */}
					<div className='rte-content-area' style={contentAreaStyles}>
						<RichTextPlugin
							contentEditable={
								<ContentEditable
									className={editableClasses}
									aria-label={name || 'Rich text editor'}
									onFocus={onFocus}
									onBlur={onBlur}
								/>
							}
							placeholder={
								placeholder ? (
                            <div style={{ 
                                position: 'absolute',
                                top: '12px',
                                left: '16px',
                                right: '16px',
                                pointerEvents: 'none',
                                color: '#9ca3af',
                                fontStyle: 'italic',
                                fontSize: '14px'
                            }}>
										{placeholder}
									</div>
								) : null
							}
							ErrorBoundary={({ children }) => (
								<div className='rte-error-boundary'>
									<p>Editor error: An unexpected error occurred</p>
									<details className='rte-error-details'>
										<summary className='rte-error-summary'>
											Details
										</summary>
										<pre className='rte-error-pre'>{String(children)}</pre>
									</details>
								</div>
							)}
						/>

						{/* Core Plugins */}
						<HistoryPlugin />

						{/* Conditional Plugins */}
						{features.list && (
							<Suspense fallback={null}>
								<ListPluginLazy />
							</Suspense>
						)}

						{features.link && (
							<>
								<LinkPlugin />
								<FloatingLinkEditor />
							</>
						)}

						{/* Custom Plugins */}
						<OnChangePlugin onChange={handleChange} />
						<InitialValuePlugin value={value} onInitialized={() => setInitialValueApplied(true)} />
						<FeatureGatePlugin features={features} />
						<NormalizeParagraphsPlugin />
					</div>
				</LexicalComposer>
			</div>
		</div>
		</RichTextErrorBoundary>
	);
}

// ==================== EXPORTS ====================

// Export utility functions that might be useful externally
export {
	normalizeHttpsUrl,
	isValidPublicUrl,
	calculateEditorHeight,
	OPEN_LINK_EDITOR_COMMAND,
};
